/**
 * The `/fleet` module's own registry (OneFleet D-16, UI-Design §3.2): its sections, the screens under each one,
 * who may open them, and — while the screens are still being built — the OneFleet work package that delivers
 * each screen. One source for the module's routes (`pages/fleet/index.tsx`), its sub-navigation, the entries the
 * navigation registry lists for breadcrumbs and the Modules menu (`lib/nav.ts`), and the tests.
 *
 * Roles follow UI-Design §3.2 for the writes (dispatchers own dispatch, orders and partners; fleet managers vehicles,
 * drivers and analytics; maintenance the maintenance and parts; finance fuel, cost allocation and the ERP Hub's
 * capabilities; administrators settings and the ERP Hub) — and every read-all role opens every section (the
 * screens gate their actions with `can()`). The OneSphere plant GM, maintenance and executive roles keep the read
 * they had when the fleet board sat under Plant operations.
 */
import { OF_ALL, OF_CONSOLE, OF_ROLE } from "./roles";

export type FleetScreen = {
  key: string;
  /** Path under the module (no leading slash; "" is the section's own page). */
  path: string;
  /** i18n key of the label. */
  label: string;
  roles?: readonly string[];
  /** OneFleet work package that delivers the screen; absent once it is built. */
  wp?: string;
};

export type FleetSection = {
  key: string;
  /** Path segment under `/fleet` ("" for the overview). */
  path: string;
  label: string;
  roles: readonly string[];
  screens: FleetScreen[];
};

export const FLEET_BASE = "/fleet";

const OS_EXEC = ["os_executive", "os_admin"] as const;
const OS_PLANT = ["os_plant_gm", "os_maintenance"] as const;

const ADMIN = [OF_ROLE.admin, ...OS_EXEC] as const;
// the roles whose API catalogue carries every `:read` (`_READ_ALL` in of_domain/identity/catalogue.py): they open every
// section — the writes stay behind their own permissions (`can()` on the screens, the API on the routes). The
// maintenance role reads vehicles, drivers, maintenance and the ERP state only (owner's decision 2026-09-20: 100 %).
const READ_ALL = [
  OF_ROLE.dispatcher,
  OF_ROLE.fleetManager,
  OF_ROLE.finance,
  OF_ROLE.analyst,
  ...ADMIN,
] as const;
const DISPATCH = [...READ_ALL] as const;
const VEHICLES = [...READ_ALL, OF_ROLE.maintenance, ...OS_PLANT] as const;
const DRIVERS = [
  ...READ_ALL,
  OF_ROLE.maintenance,
  "os_plant_gm",
  "os_hr",
] as const;
const MAINTENANCE = [
  ...READ_ALL,
  OF_ROLE.maintenance,
  "os_maintenance",
] as const;
const FUEL = [...READ_ALL, "os_finance"] as const;
const PARTNERS = [...READ_ALL] as const;
const ANALYTICS = [...READ_ALL, "os_finance", "os_plant_gm"] as const;
// The ERP Hub is an administrator's screen: connections and their credentials, the capability contract, the
// sync state, the reconciliation and the dead letter queue. A dispatcher, a fleet manager, finance or
// maintenance has no business in it — what they need from the ERP reaches them on their own screens
// (the record's ERP card, the proposals inbox). Owner's rule, 2026-09-28.
const ERP = [OF_ROLE.admin, "os_admin"] as const;
const COPILOT = [...OF_CONSOLE, ...OS_EXEC] as const; // the OneSphere administrators too (D-51 — every section)

/** Module gate: every OneFleet role plus the OneSphere roles that could already open the fleet board. */
export const FLEET_MODULE_ROLES: readonly string[] = [
  ...OF_ALL,
  ...OS_PLANT,
  ...OS_EXEC,
];

/** The people who work in this console (the shell's notification bell, D-80): not the drivers and customers, who
 * have their own apps and no in-app inbox here. */
export const FLEET_CONSOLE_ROLES: readonly string[] = [
  ...OF_CONSOLE,
  ...OS_PLANT,
  ...OS_EXEC,
];

const s = (
  key: string,
  path: string,
  label: string,
  wp?: string,
  roles?: readonly string[],
): FleetScreen => ({ key, path, label, wp, roles });

export const FLEET_SECTIONS: FleetSection[] = [
  {
    key: "overview",
    path: "",
    label: "nav.fleet.overview",
    roles: FLEET_MODULE_ROLES,
    screens: [],
  },
  {
    key: "dispatch",
    path: "dispatch",
    label: "nav.fleet.dispatch",
    roles: DISPATCH,
    screens: [
      s("board", "", "fleet.screens.dispatch.board"),
      s("orders", "orders", "fleet.screens.dispatch.orders"),
      s("runs", "runs", "fleet.screens.dispatch.runs"),
      s("trips", "trips", "fleet.screens.dispatch.trips"),
      s("map", "map", "fleet.screens.dispatch.map"),
      s("rates", "rates", "fleet.screens.dispatch.rates"),
      s(
        "service-areas",
        "service-areas",
        "fleet.screens.dispatch.serviceAreas",
      ),
      s("quotes", "quotes", "fleet.screens.dispatch.quotes", "WP-U5"),
    ],
  },
  {
    key: "vehicles",
    path: "vehicles",
    label: "nav.fleet.vehicles",
    roles: VEHICLES,
    screens: [
      s("list", "", "fleet.screens.vehicles.list"),
      s("fleets", "fleets", "fleet.screens.vehicles.fleets"),
      s("assets", "assets", "fleet.screens.vehicles.assets"),
      s("warranties", "warranties", "fleet.screens.vehicles.warranties"),
      s("telematics", "telematics", "fleet.screens.vehicles.telematics"),
    ],
  },
  {
    key: "drivers",
    path: "drivers",
    label: "nav.fleet.drivers",
    roles: DRIVERS,
    screens: [
      s("list", "", "fleet.screens.drivers.list"),
      s("schedule", "schedule", "fleet.screens.drivers.schedule"),
      s("templates", "templates", "fleet.screens.drivers.templates"),
      s("availability", "availability", "fleet.screens.drivers.availability"),
      s("time-off", "time-off", "fleet.screens.drivers.timeOff"),
      s("scorecards", "scorecards", "fleet.screens.drivers.scorecards"),
    ],
  },
  {
    key: "maintenance",
    path: "maintenance",
    label: "nav.fleet.maintenance",
    roles: MAINTENANCE,
    screens: [
      s("work-orders", "", "fleet.screens.maintenance.workOrders"),
      s("schedules", "schedules", "fleet.screens.maintenance.schedules"),
      s("inspections", "inspections", "fleet.screens.maintenance.inspections"),
      s("issues", "issues", "fleet.screens.maintenance.issues"),
      s("parts", "parts", "fleet.screens.maintenance.parts"),
    ],
  },
  {
    key: "fuel",
    path: "fuel",
    label: "nav.fleet.fuel",
    roles: FUEL,
    screens: [
      s("reports", "", "fleet.screens.fuel.reports"),
      s("transactions", "transactions", "fleet.screens.fuel.transactions"),
      s("matching", "matching", "fleet.screens.fuel.matching"),
      s("anomalies", "anomalies", "fleet.screens.fuel.anomalies"),
      s("allocation", "allocation", "fleet.screens.fuel.allocation"),
    ],
  },
  {
    key: "partners",
    path: "partners",
    label: "nav.fleet.partners",
    roles: PARTNERS,
    screens: [
      s("customers", "", "fleet.screens.partners.customers"),
      s("contacts", "contacts", "fleet.screens.partners.contacts"),
      s("vendors", "vendors", "fleet.screens.partners.vendors"),
      s("places", "places", "fleet.screens.partners.places"),
    ],
  },
  {
    key: "analytics",
    path: "analytics",
    label: "nav.fleet.analytics",
    roles: ANALYTICS,
    screens: [
      s("dashboards", "", "fleet.screens.analytics.dashboards"),
      s("reports", "reports", "fleet.screens.analytics.reports"),
    ],
  },
  {
    key: "erp",
    path: "erp",
    label: "nav.fleet.erp",
    roles: ERP,
    screens: [
      s("status", "", "fleet.screens.erp.status"),
      s("connections", "connections", "fleet.screens.erp.connections"),
      s("capabilities", "capabilities", "fleet.screens.erp.capabilities"),
      s("sync", "sync", "fleet.screens.erp.sync"),
      s("reconcile", "reconcile", "fleet.screens.erp.reconcile"),
      s("mappings", "mappings", "fleet.screens.erp.mappings"),
      s("dead-letter", "dead-letter", "fleet.screens.erp.deadLetter"),
    ],
  },
  {
    key: "copilot",
    path: "copilot",
    label: "nav.fleet.copilot",
    roles: COPILOT,
    screens: [
      s("proposals", "", "fleet.screens.copilot.proposals"),
      s("sessions", "sessions", "fleet.screens.copilot.sessions"),
      s("tasks", "tasks", "fleet.screens.copilot.tasks"),
      s(
        "policies",
        "policies",
        "fleet.screens.copilot.policies",
        undefined,
        ADMIN,
      ),
      s("alerts", "alerts", "fleet.screens.copilot.alerts"),
    ],
  },
  {
    key: "settings",
    path: "settings",
    label: "nav.fleet.settings",
    roles: ADMIN,
    screens: [
      s("general", "", "fleet.screens.settings.general"),
      s("order-types", "order-types", "fleet.screens.settings.orderTypes"),
      s(
        "custom-fields",
        "custom-fields",
        "fleet.screens.settings.customFields",
      ),
      s("driver-app", "driver-app", "fleet.screens.settings.driverApp"),
      s(
        "notifications",
        "notifications",
        "fleet.screens.settings.notifications",
      ),
      s("documents", "documents", "fleet.screens.settings.documents"),
      s("branding", "branding", "fleet.screens.settings.branding"),
      s("ai", "ai", "fleet.screens.settings.ai"),
      s("integrations", "integrations", "fleet.screens.settings.integrations"),
      s("security", "security", "fleet.screens.settings.security"),
      s("developer", "developer", "fleet.screens.settings.developer"),
    ],
  },
];

/** Absolute path of a section, or of a screen inside it. */
export function fleetPath(section: FleetSection, screen?: FleetScreen): string {
  const parts = [FLEET_BASE, section.path, screen?.path].filter(
    (p) => p && p.length > 0,
  );
  return parts.join("/");
}

export function sectionByKey(key: string): FleetSection | undefined {
  return FLEET_SECTIONS.find((x) => x.key === key);
}

/** The section a pathname under `/fleet` belongs to (the overview for `/fleet` itself). */
export function sectionForPath(pathname: string): FleetSection | undefined {
  const rest = pathname.startsWith(FLEET_BASE)
    ? pathname.slice(FLEET_BASE.length).split("/").filter(Boolean)
    : null;
  if (rest === null) return undefined;
  const seg = rest[0] ?? "";
  return FLEET_SECTIONS.find((x) => x.path === seg);
}
